import { Suspense, lazy } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';

import { RequireSession } from '../auth/RequireSession';
import { HomeScreen } from '../features/home/HomeScreen';
import LedgerScreen from '../features/ledger/LedgerScreen';
import NewEntryScreen from '../features/ledger/NewEntryScreen';
import { routePaths } from './routes';

/**
 * The one route outside the initial chunk when the router landed (`ARCH-005 §2.1`). `lazy()` is the
 * boundary the whole splitting strategy rests on, so the app has one from the first commit that has a
 * router at all — a boundary added later is a boundary discovered to be impossible later.
 */
const NotFoundScreen = lazy(async () => import('../features/notFound/NotFoundScreen'));

/** `LED-04`. Reached from a row rather than opened directly, so it is behind a `lazy()` (`STD-002 §8`). */
const EntryScreen = lazy(async () => import('../features/ledger/EntryScreen'));

/** `LED-07`. Reached from settings and from an undo, rarely — behind a `lazy()` (`STD-002 §8`). */
const TrashScreen = lazy(async () => import('../features/ledger/TrashScreen'));

/** `ONB-01`. Signed-out only, seen once — exactly the shape `STD-002 §8` puts behind a `lazy()`. */
const WelcomeScreen = lazy(async () => import('../features/onboarding/WelcomeScreen'));

/** `ONB-02`. */
const CreateAccountScreen = lazy(async () => import('../features/onboarding/CreateAccountScreen'));

/** `ONB-03`. */
const VerifyEmailScreen = lazy(async () => import('../features/onboarding/VerifyEmailScreen'));

/** `ONB-04`. */
const SignInScreen = lazy(async () => import('../features/onboarding/SignInScreen'));

/** The two steps behind `ONB-04`'s "Forgot password?" (`FEAT-005 §2`). */
const ResetPasswordScreen = lazy(async () => import('../features/onboarding/ResetPasswordScreen'));

/** `ONB-05`, `ONB-06`, `ONB-07` — the three screens between a proved address and the ledger. */
const RegionScreen = lazy(async () => import('../features/onboarding/RegionScreen'));
const StorageModeScreen = lazy(async () => import('../features/onboarding/StorageModeScreen'));
const TrialStartedScreen = lazy(async () => import('../features/onboarding/TrialStartedScreen'));

/**
 * Every route in the app, as a tree.
 *
 * It is exported separately from `AppRouter` so that a test can mount the real tree under a
 * `MemoryRouter` instead of copying it. A copied route tree is a test that passes while the app 404s —
 * it was one before, and adding `ONB-01` was the second screen to notice.
 */
export function AppRoutes(): React.JSX.Element {
  return (
    // A lazy route needs something to show while its chunk arrives. Null rather than a spinner: on a
    // fast connection a spinner appears and vanishes, which reads as a flicker rather than as progress
    // (`STD-002 §3.4` is about a component's own states; this is the gap between two of them).
    <Suspense fallback={null}>
      <Routes>
        {/*
          Home is the app, so it is behind a session. The onboarding routes below are not: somebody
          without one has to be able to reach them, which is the whole point of them.
        */}
        <Route
          path={routePaths.home}
          element={
            <RequireSession>
              <HomeScreen />
            </RequireSession>
          }
        />
        {/*
          `LED-02`. In the initial chunk with Home (`STD-002 §8`): it is reached from the `+` on every
          ledger screen and is what the product is for, so fetching a chunk on the first tap would put a
          round trip between somebody and the five seconds this is meant to take.
        */}
        <Route
          path={routePaths.addEntry}
          element={
            <RequireSession>
              <NewEntryScreen />
            </RequireSession>
          }
        />
        <Route
          path={routePaths.ledger}
          element={
            <RequireSession>
              <LedgerScreen />
            </RequireSession>
          }
        />
        <Route
          path={routePaths.trash}
          element={
            <RequireSession>
              <TrashScreen />
            </RequireSession>
          }
        />
        <Route
          path={routePaths.entry}
          element={
            <RequireSession>
              <EntryScreen />
            </RequireSession>
          }
        />
        <Route path={routePaths.welcome} element={<WelcomeScreen />} />
        <Route path={routePaths.createAccount} element={<CreateAccountScreen />} />
        <Route path={routePaths.verifyEmail} element={<VerifyEmailScreen />} />
        <Route path={routePaths.signIn} element={<SignInScreen />} />
        <Route path={routePaths.resetPassword} element={<ResetPasswordScreen />} />

        {/*
          Onboarding needs a session: all three write to or read from `/me` (`FEAT-008 §7`). They are not
          part of the signed-out surface above, which is what somebody without an account has to reach.
        */}
        <Route
          path={routePaths.region}
          element={
            <RequireSession>
              <RegionScreen />
            </RequireSession>
          }
        />
        <Route
          path={routePaths.storageMode}
          element={
            <RequireSession>
              <StorageModeScreen />
            </RequireSession>
          }
        />
        <Route
          path={routePaths.trialStarted}
          element={
            <RequireSession>
              <TrialStartedScreen />
            </RequireSession>
          }
        />
        <Route path={routePaths.notFound} element={<NotFoundScreen />} />
      </Routes>
    </Suspense>
  );
}

/**
 * The router (`CR-006`, `ARCH-001 §4`).
 *
 * `BrowserRouter` rather than the data router: this app's data lives in TanStack Query and Dexie
 * (`STD-002 §4`), so route loaders would be a second place for server state to live and a rule that says
 * "server state in TanStack Query" would immediately have an exception.
 *
 * Paths come from `routePaths` and are written nowhere else.
 */
export function AppRouter(): React.JSX.Element {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
